import React from 'react';
import { cn } from '@/lib/utils';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

export interface CalendarProps {
    mode?: 'single' | 'range';
    selected?: Date;
    onSelect?: (date: Date | undefined) => void;
    initialFocus?: boolean;
    className?: string;
}

export const Calendar: React.FC<CalendarProps> = ({
    mode = 'single',
    selected,
    onSelect,
    initialFocus = false,
    className,
}) => {
    const [currentMonth, setCurrentMonth] = React.useState(new Date());

    const today = new Date();
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();

    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);
    const firstDayOfWeek = firstDayOfMonth.getDay();
    const daysInMonth = lastDayOfMonth.getDate();

    const monthNames = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
    ];

    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    const handleDateClick = (day: number) => {
        const date = new Date(year, month, day);
        onSelect?.(date);
    };

    const goToPreviousMonth = () => {
        setCurrentMonth(new Date(year, month - 1, 1));
    };

    const goToNextMonth = () => {
        setCurrentMonth(new Date(year, month + 1, 1));
    };

    const isToday = (day: number) => {
        const date = new Date(year, month, day);
        return date.toDateString() === today.toDateString();
    };

    const isSelected = (day: number) => {
        if (!selected) return false;
        const date = new Date(year, month, day);
        return date.toDateString() === selected.toDateString();
    };

    const renderDays = () => {
        const days = [];

        // Empty cells for days before the first day of the month
        for (let i = 0; i < firstDayOfWeek; i++) {
            days.push(<div key={`empty-${i}`} className="h-9 w-9" />);
        }

        // Days of the month
        for (let day = 1; day <= daysInMonth; day++) {
            days.push(
                <Button
                    key={day}
                    variant="ghost"
                    className={cn(
                        'h-9 w-9 p-0 font-normal',
                        isToday(day) && 'bg-accent text-accent-foreground',
                        isSelected(day) &&
                            'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground',
                        !isToday(day) &&
                            !isSelected(day) &&
                            'hover:bg-accent hover:text-accent-foreground'
                    )}
                    onClick={() => handleDateClick(day)}
                >
                    {day}
                </Button>
            );
        }

        return days;
    };

    return (
        <div className={cn('rounded-md border bg-background p-3', className)}>
            <div className="flex items-center justify-between mb-4">
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={goToPreviousMonth}
                    className="h-7 w-7 p-0"
                >
                    <ChevronLeft className="h-4 w-4" />
                </Button>
                <div className="text-sm font-medium">
                    {monthNames[month]} {year}
                </div>
                <Button variant="ghost" size="sm" onClick={goToNextMonth} className="h-7 w-7 p-0">
                    <ChevronRight className="h-4 w-4" />
                </Button>
            </div>

            <div className="grid grid-cols-7 gap-1 mb-2">
                {dayNames.map(day => (
                    <div
                        key={day}
                        className="text-center text-xs text-muted-foreground font-medium"
                    >
                        {day}
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-7 gap-1">{renderDays()}</div>
        </div>
    );
};

export default Calendar;
